import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Product catalog — the sellable products used to build a contract's order.
 * Mirrors the legacy contract-create flow: a customer exposes interested product
 * GROUPS (`/customers/:id/service-interestings`), each group lists PRODUCTS
 * (`/products/groups?group_name=`), and a product's full detail (pricing config
 * template, price model) comes from `/products/:id`.
 */

/** A product group a customer is interested in (drives the group dropdown). */
export interface ServiceInteresting {
  product_group_name: string;
  product_group_code?: string;
  is_addon?: boolean;
  [key: string]: unknown;
}

/** A product within a group (the product dropdown option). */
export interface Product {
  product_id: number;
  product_name: string;
  product_group_name: string;
  product_group_code?: string;
  /** Per-type template dispatcher key, e.g. "Config.Bandwidth", "Cloud.VCloud". */
  template_name?: string;
  service_price_model?: string;
  service_price_register?: number;
  is_hidden?: boolean;
  is_addon?: boolean;
  product_config_options?: unknown[];
  [key: string]: unknown;
}

/**
 * `GET /customers/:id/service-interestings` — product groups for a customer.
 * The backend returns `results` as a plain STRING array of group names
 * (e.g. `["CLOUD SERVICE"]`), not objects — normalize each to a
 * `{ product_group_name }` so consumers can read a stable field.
 */
export function useCustomerServiceInterestings(customerId: number) {
  return useQuery({
    queryKey: ['customers', customerId, 'service-interestings'],
    enabled: !!customerId,
    queryFn: async (): Promise<ServiceInteresting[]> => {
      const res = await dataCentralApi.get<ResponseArray<string | ServiceInteresting>>(
        `/customers/${customerId}/service-interestings`,
      );
      return (res.data.results ?? [])
        .map((row) => (typeof row === 'string' ? { product_group_name: row } : row))
        .filter((g) => !!g.product_group_name);
    },
  });
}

/** `GET /products/groups?group_name=&is_addon=` — products in a group. */
export function useProductsInGroup(groupName: string, isAddon = false) {
  return useQuery({
    queryKey: ['products', 'groups', groupName, isAddon],
    enabled: !!groupName,
    queryFn: async (): Promise<Product[]> => {
      const res = await dataCentralApi.get<ResponseArray<Product>>(
        `/products/groups?${qs.stringify({ group_name: groupName, is_addon: isAddon })}`,
      );
      return res.data.results ?? [];
    },
  });
}

/** `GET /products/:id` — full product detail (price model + config template). */
export function useProductDetail(productId: number) {
  return useQuery({
    queryKey: ['products', productId],
    enabled: !!productId,
    queryFn: async (): Promise<Product | null> => {
      const res = await dataCentralApi.get<ResponseObject<Product>>(`/products/${productId}`);
      return res.data.result ?? null;
    },
  });
}

/** A domain TLD price row (`GET /products/domain-prices`), for the Domain template. */
export interface DomainPrice {
  tld?: string;
  register_price?: number;
  renew_price?: number;
  transfer_price?: number;
  [key: string]: unknown;
}

/** `GET /products/domain-prices` — TLD pricing (Domain product templates only). */
export function useDomainPrices(enabled = true) {
  return useQuery({
    queryKey: ['products', 'domain-prices'],
    enabled,
    queryFn: async (): Promise<DomainPrice[]> => {
      const res = await dataCentralApi.get<ResponseArray<DomainPrice>>('/products/domain-prices');
      return res.data.results ?? [];
    },
  });
}
